import { useCallback, useEffect, useMemo } from 'react';
import { alpha, Unstable_Grid2 as Grid } from '@mui/material';
import { FieldArray } from 'react-final-form-arrays';
import { Field, useForm, useFormState } from 'react-final-form';

import {
  AddIcon,
  Button,
  DeleteForeverOutlinedIcon,
  IconButton,
  Switch,
} from '@occc/ui-design-system';
import { TextField, Select } from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { parseNumber } from '@occc-app/lib/finalFormUtils';

import type {
  TransactionFrequencyLimitRow,
  TransactionFrequencyTimePeriod,
} from './transaction-frequency-limit.types';
import {
  getFormValueByPath,
  getNextAvailablePeriod,
  getNormalizedTransactionFrequencyFormValue,
  hasValidTransactionFrequencyRowsArray,
} from './transaction-frequency-limit.utils';
import { periodOptions } from './transaction-frequency-limit.constants';

import { LimitFormContentLayout } from '../components/limit-form-content-layout';
import type { LimitFormNamePaths } from '../types';

const TRANSACTION_FREQUENCY_FORM_NAME_PATH =
  'awardCount.transactionFrequency' satisfies LimitFormNamePaths;

type TransactionFrequencyRowFieldSuffix = '.limit' | '.timePeriod';

type TransactionFrequencyLimitProps = {
  open: boolean;
  periodOptionsToUse?: { label: string; value: TransactionFrequencyTimePeriod }[];
  /** When provided, used for new rows and when normalizing invalid periods. Omit to derive from first option or fallback to TOTAL. */
  defaultPeriod?: TransactionFrequencyTimePeriod;
};

export const TransactionFrequencyLimit = ({
  open,
  periodOptionsToUse = periodOptions,
  defaultPeriod: defaultPeriodProp,
}: TransactionFrequencyLimitProps) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const formValue = getFormValueByPath(values, TRANSACTION_FREQUENCY_FORM_NAME_PATH);
  const hasValidRowsArray = hasValidTransactionFrequencyRowsArray(formValue);
  const rows: TransactionFrequencyLimitRow[] = useMemo(
    () => (formValue && formValue.isEnabled && 'rows' in formValue ? formValue.rows : []),
    [formValue],
  );
  const { isEditable } = useCanEditFormFieldsContext();
  const defaultPeriod = defaultPeriodProp ?? periodOptionsToUse[0]?.value ?? 'TOTAL';

  useEffect(() => {
    if (hasValidRowsArray) return;
    const transactionFrequencyFormValue = getNormalizedTransactionFrequencyFormValue(
      form.getState().values,
      TRANSACTION_FREQUENCY_FORM_NAME_PATH,
    );
    form.change(TRANSACTION_FREQUENCY_FORM_NAME_PATH, transactionFrequencyFormValue);
  }, [form, hasValidRowsArray]);

  const ensureDefaultRowWhenEnabling = useCallback(() => {
    const values = form.getState().values;
    const current = getFormValueByPath(values, TRANSACTION_FREQUENCY_FORM_NAME_PATH);
    const currentRows = current?.rows;
    if (Array.isArray(currentRows) && currentRows.length === 0) {
      form.change(`${TRANSACTION_FREQUENCY_FORM_NAME_PATH}.rows`, [
        { limit: 1, timePeriod: defaultPeriod },
      ]);
    }
  }, [form, defaultPeriod]);

  useEffect(() => {
    const values = form.getState().values;
    const currentFormValue = getFormValueByPath(values, TRANSACTION_FREQUENCY_FORM_NAME_PATH);

    if (!currentFormValue?.isEnabled || !('rows' in currentFormValue)) return;

    const { rows } = currentFormValue;

    rows.forEach((row, i) => {
      const isAllowed = periodOptionsToUse.some((o) => o.value === row.timePeriod);
      if (!isAllowed) {
        form.change(`${TRANSACTION_FREQUENCY_FORM_NAME_PATH}.rows[${i}].timePeriod`, defaultPeriod);
      }
    });
  }, [form, formValue?.isEnabled, periodOptionsToUse, defaultPeriod, rows.length]);

  return (
    <LimitFormContentLayout
      title='Transaction Frequency Limit'
      description='Limit how many transactions a member can make for this offer (total or per day, week, or month).'
      action={
        <Field
          name={'awardCount.transactionFrequency.isEnabled' satisfies LimitFormNamePaths}
          type='checkbox'
        >
          {({ input }) => (
            <Switch
              name={input.name}
              checked={Boolean(input.checked)}
              onChange={(e) => {
                input.onChange(e);
                if (e.target.checked) {
                  ensureDefaultRowWhenEnabling();
                }
              }}
              disabled={!isEditable}
            />
          )}
        </Field>
      }
      open={open}
    >
      {hasValidRowsArray && (
        <FieldArray name={'awardCount.transactionFrequency.rows' satisfies LimitFormNamePaths}>
          {({ fields }) => {
            return (
              <>
                {(fields.length ?? 0) === 0 && (
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant='contained'
                        startIcon={<AddIcon />}
                        onClick={() => fields.push({ limit: 1, timePeriod: defaultPeriod })}
                        sx={(theme) => ({
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.25),
                          },
                        })}
                      >
                        ADD LIMIT
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {fields.map((name, index) => {
                  const canRemove = (fields.length ?? 0) > 1;
                  const isLastRow = index === (fields.length ?? 1) - 1;
                  const canAddMore = (fields.length ?? 0) < periodOptionsToUse.length;
                  const showAddButtonInRow = isLastRow && canAddMore;
                  const addButtonSize = canRemove ? 5 : 6;

                  return (
                    <Grid key={name} container spacing={3} sx={{ mb: 2 }} alignItems='flex-start'>
                      <Grid xs={12} sm={3}>
                        <TextField
                          name={`${name}${'.limit' satisfies TransactionFrequencyRowFieldSuffix}`}
                          label='Transactions'
                          fieldProps={{
                            parse: parseNumber,
                          }}
                        />
                      </Grid>
                      <Grid xs={12} sm={3}>
                        <Select
                          name={`${name}${
                            '.timePeriod' satisfies TransactionFrequencyRowFieldSuffix
                          }`}
                          label='Frequency'
                          data={periodOptionsToUse}
                          formControlProps={{ size: 'medium', margin: 'none' }}
                        />
                      </Grid>
                      {canRemove && (
                        <Grid xs={12} sm={1}>
                          <IconButton
                            onClick={() => fields.remove(index)}
                            size='medium'
                            aria-label='Remove limit'
                            sx={(theme) => ({
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.25),
                              },
                            })}
                          >
                            <DeleteForeverOutlinedIcon color='error' fontSize='inherit' />
                          </IconButton>
                        </Grid>
                      )}
                      {showAddButtonInRow && (
                        <Grid
                          xs={12}
                          sm={addButtonSize}
                          sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          <Button
                            variant='contained'
                            startIcon={<AddIcon />}
                            onClick={() => {
                              fields.push({
                                limit: 1,
                                timePeriod: getNextAvailablePeriod(
                                  rows,
                                  periodOptionsToUse,
                                  defaultPeriod,
                                ),
                              } satisfies TransactionFrequencyLimitRow);
                            }}
                            sx={(theme) => ({
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.25),
                              },
                            })}
                          >
                            ADD ANOTHER LIMIT
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </>
            );
          }}
        </FieldArray>
      )}
    </LimitFormContentLayout>
  );
};
